import React,{useState,useEffect,useMemo} from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import './shop.css'

/* ------------------------------------------------------------------
   ICONS
   ------------------------------------------------------------------ */
const IconEdit=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>)
const IconX=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>)
const IconEye=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>)
const IconEyeOff=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3l18 18"/><path d="M10.6 6.2A9.8 9.8 0 0 1 12 6c6.4 0 10 6 10 6a17 17 0 0 1-3.2 3.9"/><path d="M6.2 7.2A17 17 0 0 0 2 12s3.6 7 10 7a9.7 9.7 0 0 0 4-.8"/><path d="M9.9 10a3 3 0 0 0 4.2 4.2"/></svg>)

/* ------------------------------------------------------------------
   THEME TOKENS FOR THE DATA GRID — see EShopProducts for the why.
   ------------------------------------------------------------------ */
const TOKEN_NAMES=[
  '--panel','--panel-raise','--line','--line-soft',
  '--ink','--ink-2','--ink-3','--accent','--accent-wash',
  '--mono','--sans','--step-0'
]

const readTokens=()=>{
  const out={}
  if (typeof window==='undefined'||!document.documentElement) return out
  const styles=window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach(name=>{ out[name]=String(styles.getPropertyValue(name)||'').trim() })
  return out
}

function useTableStyles(){
  const [tokens,setTokens]=useState(readTokens)

  useEffect(()=>{
    let frame=null
    const refresh=()=>{
      if (frame) window.cancelAnimationFrame(frame)
      frame=window.requestAnimationFrame(()=>setTokens(readTokens()))
    }

    const observer=new MutationObserver(refresh)
    observer.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme','class','style']})

    let media=null
    if (window.matchMedia){
      media=window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change',refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return ()=>{
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media){
        if (media.removeEventListener) media.removeEventListener('change',refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  },[])

  return useMemo(()=>{
    const t=tokens
    return {
      table:{ style:{ backgroundColor:t['--panel'], color:t['--ink'] } },
      tableWrapper:{ style:{ display:'block' } },
      head:{ style:{ color:t['--ink-3'] } },
      headRow:{
        style:{
          backgroundColor:t['--panel-raise'],
          color:t['--ink-3'],
          minHeight:'42px',
          borderBottomWidth:'1px',
          borderBottomColor:t['--line'],
          borderBottomStyle:'solid'
        }
      },
      headCells:{
        style:{
          paddingLeft:'12px',
          paddingRight:'12px',
          fontFamily:t['--mono'],
          fontSize:'10px',
          fontWeight:600,
          letterSpacing:'0.08em',
          textTransform:'uppercase',
          color:t['--ink-3']
        }
      },
      rows:{
        style:{
          backgroundColor:t['--panel'],
          color:t['--ink'],
          fontFamily:t['--sans'],
          fontSize:t['--step-0']||'0.875rem',
          minHeight:'52px',
          '&:not(:last-of-type)':{
            borderBottomWidth:'1px',
            borderBottomStyle:'solid',
            borderBottomColor:t['--line-soft']
          }
        },
        highlightOnHoverStyle:{
          backgroundColor:t['--accent-wash'],
          color:t['--ink'],
          borderBottomColor:t['--line-soft'],
          outlineWidth:'0px',
          outlineColor:t['--panel'],
          transitionDuration:'0.15s',
          transitionProperty:'background-color'
        }
      },
      cells:{ style:{ paddingLeft:'12px', paddingRight:'12px' } },
      noData:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'], padding:'26px' } },
      progress:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } }
    }
  },[tokens])
}

/* A live secret should not sit in plain sight on a shared screen. */
function Secret({value,label}){
  const [open,setOpen]=useState(false)
  const text=String(value===undefined||value===null?'':value)
  if (!text) return <span className="sh-cell-mute">—</span>
  const masked='•'.repeat(Math.min(text.length,24))

  return (
    <span className="sh-secret">
      <span className="sh-secret-v" data-hidden={open?'0':'1'}>{open?text:masked}</span>
      <button
        type="button"
        className="sh-icon-btn"
        onClick={()=>setOpen(v=>!v)}
        aria-label={(open?'Hide ':'Show ')+(label||'value')}
        aria-pressed={open}
      >
        {open?<IconEyeOff/>:<IconEye/>}
      </button>
    </span>
  )
}

/* Token-styled dialog. Escape closes, backdrop closes, full-bleed under 560px. */
function Sheet({open,title,eyebrow,subtitle,size,onClose,children}){
  useEffect(()=>{
    if (!open) return undefined
    const onKey=(e)=>{ if (e.key==='Escape') onClose() }
    document.addEventListener('keydown',onKey)
    return ()=>document.removeEventListener('keydown',onKey)
  },[open,onClose])

  if (!open) return null

  return (
    <div className="sh-back" onMouseDown={(e)=>{ if (e.target===e.currentTarget) onClose() }}>
      <div className={size?'sh-modal '+size:'sh-modal'} role="dialog" aria-modal="true" aria-label={title}>
        <div className="sh-modal-head">
          <div className="sh-modal-headline">
            {eyebrow?<span className="sh-modal-eyebrow">{eyebrow}</span>:null}
            <h2 className="sh-modal-title">{title}</h2>
            {subtitle?<div className="sh-modal-sub">{subtitle}</div>:null}
          </div>
          <button type="button" className="sh-modal-x" onClick={onClose} aria-label="Close"><IconX/></button>
        </div>
        <div className="sh-modal-body">{children}</div>
      </div>
    </div>
  )
}

export default function EShopSettings() {
    const [data,setData]=useState([])
    const [isLoading,setIsLoading]=useState(true)
    const [show, setShow] = useState(false);
    const [record,setRecord]=useState({})

    /* inline feedback — this is what replaced alert() */
    const [note,setNote]=useState(null)
    const [formNote,setFormNote]=useState(null)
    const [keyOpen,setKeyOpen]=useState(false)

    const tableStyles=useTableStyles()

    const columns = [
        {
            name: '#',
            selector: (row,index) =>  index+1,
            sortable: false,
            width:'56px'
        },
        {
            name: 'EShop Url',
            selector: row =>  row.EShopUrl,
            cell: row => <span className="sh-cell-strong">{row.EShopUrl}</span>,
            sortable: true,
            minWidth:'260px'
        },
        {
            name: 'Stripe Key',
            selector: row =>  row.StripeKey,
            cell: row => <Secret value={row.StripeKey} label="Stripe key"/>,
            sortable: false,
            minWidth:'240px'
        },
        {
            name: '',
            cell: row => (
                <button type="button" className="sh-icon-btn" onClick={()=>showModal(row.id)} aria-label="Edit settings">
                    <IconEdit/>
                </button>
            ),
            sortable: false,
            width:'70px',
            right:true
        }
    ]

    const handleClose = () => setShow(false);

    useEffect(()=>{
        loadData()
    },[])


      const loadData=()=>{
        setIsLoading(true)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/eshopsettings',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{

          setData(res.data.results)
          setIsLoading(false)

        }).catch(err=>{
            setIsLoading(false)
            setNote({tone:'crit',text:'The settings could not be loaded. Check your connection and try again.'})
            console.log(err)
        })
      }



      const showModal=(id)=>{

            const recObj=data.filter(item=>item.id==id)[0]
            if (!recObj) return

            setFormNote(null)
            setKeyOpen(false)
            setRecord({...recObj,ModalTitle:''})
            setShow(true)


      }

      const save=(e)=>{
        e.preventDefault()

        axios.post(process.env.REACT_APP_API_SERVER+'admin/eshopsettings',record,{
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                }
            })
          .then(res=>{
            if (res.data.err==0){
                loadData()
                sessionStorage.setItem('StripeKey',record.StripeKey)
                sessionStorage.setItem('EShopUrl',record.EShopUrl)
                setShow(false)
                setNote({tone:'ok',text:res.data.msg})
            }else{
                setFormNote({tone:'crit',text:res.data.msg})
            }
        }).catch(err=>{
            setFormNote({tone:'crit',text:'Saving failed. Please try again.'})
            console.log(err)
        })
      }

      const rows=Array.isArray(data)?data:[]

    return (
        <div className="sh-page">

            {note
              ? <div className="sh-note" data-tone={note.tone} role="status">
                  <span className="sh-note-text">{note.text}</span>
                  <button type="button" className="sh-note-x" aria-label="Close" onClick={()=>setNote(null)}><IconX/></button>
                </div>
              : null}

            <Panel
              title="EShop Settings"
              subtitle="The Stripe key and the eshop address that every checkout link is built from."
            >
              <p className="callout">
                The <b>EShop Url</b> is the base of every purchase link: a product sells at
                <b> {'{EShop Url}'}/#/checkout/{'{UUID}'}</b>. Change it here and every link on the
                EShop Products page changes automatically.
              </p>

              {isLoading
                ? <div className="sh-sk" aria-hidden="true">
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                  </div>
                : rows.length===0
                  ? <div className="sh-empty">
                      <div className="sh-empty-t">No settings</div>
                      <div className="sh-empty-s">
                        No settings record was returned. Without it the eshop cannot
                        produce payment links.
                      </div>
                      <div className="sh-empty-a">
                        <Btn onClick={loadData}>Try again</Btn>
                      </div>
                    </div>
                  : <div className="sh-table">
                      <DataTable
                        columns={columns}
                        data={rows}
                        customStyles={tableStyles}
                        highlightOnHover
                        persistTableHead
                        noDataComponent={<div className="sh-empty-s">No settings.</div>}
                      />
                    </div>}
            </Panel>

            <Sheet
              open={show}
              size="sh-modal-lg"
              eyebrow="EShop Settings"
              title="Payment Settings"
              subtitle="These apply immediately to every new purchase."
              onClose={handleClose}
            >
              {formNote
                ? <div className="sh-note" data-tone={formNote.tone} role="alert">
                    <span className="sh-note-text">{formNote.text}</span>
                    <button type="button" className="sh-note-x" aria-label="Close" onClick={()=>setFormNote(null)}><IconX/></button>
                  </div>
                : null}

              <form className="sh-form" onSubmit={save}>

                <fieldset className="sh-fieldset">
                  <legend className="sh-legend">Stripe</legend>

                  <div className="sh-field">
                    <div className="sh-label">
                      <label htmlFor="es-key">Stripe Key</label>
                      <button
                        type="button"
                        className="sh-linkish"
                        onClick={()=>setKeyOpen(v=>!v)}
                        aria-pressed={keyOpen}
                      >
                        {keyOpen?'Hide':'Show'}
                      </button>
                    </div>
                    <input
                      id="es-key"
                      className="sh-input sh-mono"
                      type={keyOpen?'text':'password'}
                      autoComplete="off"
                      spellCheck="false"
                      value={record.StripeKey===undefined||record.StripeKey===null?'':record.StripeKey}
                      onChange={(e)=>setRecord({...record,StripeKey:e.target.value})}
                    />
                    <div className="sh-hint">The secret key that purchases are charged with. Do not share it.</div>
                  </div>
                </fieldset>

                <fieldset className="sh-fieldset">
                  <legend className="sh-legend">Eshop address</legend>

                  <div className="sh-field">
                    <label className="sh-label" htmlFor="es-url">
                      EShop Url
                      <span className="sh-label-note">no trailing slash</span>
                    </label>
                    <input
                      id="es-url"
                      className="sh-input sh-mono"
                      type="text"
                      required
                      spellCheck="false"
                      placeholder="https://manager.onetwothreehigh.com"
                      value={record.EShopUrl===undefined||record.EShopUrl===null?'':record.EShopUrl}
                      onChange={(e)=>setRecord({...record,EShopUrl:e.target.value})}
                    />
                    <div className="sh-hint">
                      e.g. https://manager.onetwothreehigh.com — every checkout link starts from here.
                    </div>
                  </div>
                </fieldset>

                <div className="sh-actions">
                  <span className="sh-actions-end">
                    <Btn onClick={handleClose}>Cancel</Btn>
                    <Btn variant="accent" type="submit">Save</Btn>
                  </span>
                </div>

              </form>
            </Sheet>
        </div>
    )
}
